import type { FieldPolicy } from "@apollo/client";
import { Temporal } from "temporal-polyfill";

export const dateTypePolicy: FieldPolicy<Temporal.PlainDate, string | Date | Temporal.PlainDate, string> = {
  merge: (_, incoming) => {
    if (incoming == null) return incoming;
    return toPlainDate(incoming);
  },
};

export const dateTimeTypePolicy: FieldPolicy<Temporal.ZonedDateTime, string | Date | Temporal.ZonedDateTime, string> = {
  merge: (_, incoming) => {
    if (incoming == null) return incoming;
    return toZonedDateTime(incoming);
  },
};

/** Used for custom query resolvers that return a list of dates */
export const dateArrayTypePolicy: FieldPolicy<
  Array<Temporal.PlainDate>,
  Array<string | Date | Temporal.PlainDate>,
  string
> = {
  merge: (_, incoming) => {
    if (incoming == null) return incoming;
    return incoming.map((date) => toPlainDate(date));
  },
};

// Apollo's structural equality (@wry/equality) treats two distinct Temporal instances of the same value as
// NOT equal, so handing back a fresh instance on every response/cache read makes Apollo believe the data
// perpetually changed, causing endless reobserve/refetch loops. Interning by canonical value keeps one
// instance per date so equality short-circuits on identity. Temporal values are immutable, so sharing is safe.
const plainDateCache = new Map<string, Temporal.PlainDate>();
const zonedDateTimeCache = new Map<string, Temporal.ZonedDateTime>();
const maxInternedValues = 50_000;

/** Returns a shared instance for `value`'s canonical string form, so equal values are identity-equal. */
function intern<T extends { toString(): string }>(cache: Map<string, T>, value: T): T {
  const key = value.toString();
  const existing = cache.get(key);
  if (existing !== undefined) return existing;
  // Bound memory: evict the oldest entry (FIFO via Map insertion order) once past the cap.
  if (cache.size >= maxInternedValues) cache.delete(cache.keys().next().value!);
  cache.set(key, value);
  return value;
}

function toPlainDate(date: Date | string | Temporal.PlainDate): Temporal.PlainDate {
  // An already-parsed Temporal is returned as-is (it's typically the interned instance already).
  if (date instanceof Temporal.PlainDate) return date;
  const plainDate =
    date instanceof Date
      ? Temporal.PlainDate.from({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() })
      : Temporal.PlainDate.from(date);
  return intern(plainDateCache, plainDate);
}

function toZonedDateTime(date: Date | string | Temporal.ZonedDateTime): Temporal.ZonedDateTime {
  if (date instanceof Temporal.ZonedDateTime) return date;
  const zonedDateTime =
    date instanceof Date
      ? Temporal.Instant.fromEpochMilliseconds(date.getTime()).toZonedDateTimeISO("UTC")
      : Temporal.Instant.from(date).toZonedDateTimeISO("UTC");
  return intern(zonedDateTimeCache, zonedDateTime);
}
